/* hzo-zoek.js — de instant-zoeker: pagina's erbij, en een aantal op "View all".
 *
 * ── 1 · PAGINA'S ONTBRAKEN ────────────────────────────────────────────────
 * Zoek op "shipping" of "policy" en je kreeg niets, terwijl die pagina's er wel
 * zijn. De oorzaak zit in `assets/predictive-search.js`, `#getSearchResults()`:
 * dat zet alleen `q` en `resources[limit_scope]=each`, en géén
 * `resources[type]`. Welke soorten je dan terugkrijgt is een serverstandaard.
 *
 * Er is geen attribuut, geen sectie-instelling en geen thema-instelling om dat
 * te sturen — nagezocht in het hele thema; `resources[type]` komt er precies
 * twee keer in voor en beide keren niet hier.
 *
 * Gemeten: vraag je `resources[type]=product,collection,page` mee, dan rendert
 * Horizons eigen sectie de Pages-groep gewoon. Hij kán het dus al; er werd
 * alleen niet om gevraagd. Vandaar dat we de aanvraag onderscheppen in plaats
 * van zijn bestand te wijzigen. Verandert Horizon ooit zijn adres, dan matcht
 * onze voorwaarde niet meer en zijn we terug bij het gedrag van vandaag — geen
 * kapot scherm, alleen geen pagina's.
 *
 * ── 2 · HET AANTAL OP DE KNOP ─────────────────────────────────────────────
 * Het thema HEEFT al een knop onderaan: "View all", in
 * `.predictive-search-form__footer`. Die hoeven we niet na te bouwen; er hoort
 * alleen een getal bij.
 *
 * Waarom we in die knop schrijven en niet in het resultatenpaneel: bij elke
 * toetsaanslag doet Horizon `morph()` op het paneel, en dan is alles wat wij
 * daar neerzetten weg. De voettekst staat BUITEN dat paneel en overleeft dus.
 *
 * Het getal komt uit de regel die alleen een schermlezer voorleest
 * ("6 search results found for …"), zodat het niet uit de pas kan lopen met wat
 * Shopify daadwerkelijk gevonden heeft.
 */
(() => {
  if (window.__hzoZoek) return;
  window.__hzoZoek = true;

  const SOORTEN = 'product,collection,page';
  /* Vier per soort. Zonder limiet gaf "dress" zes collecties plus acht
     producten, en dan staat de laatste groep onder het toetsenbord. */
  const PER_SOORT = '4';

  /* ── pagina's meevragen ─────────────────────────────────────────────────
     Alleen aanvragen naar het instant-zoek-adres, en alleen als de soorten er
     nog niet in staan. Alles in een try, want de zoekfunctie mag nooit
     sneuvelen op onze eigen ingreep. */
  /* Aan `window` gebonden, net als in hzo-cart.js. Ongebonden werkte dit alleen
     doordat dit bestand als klassiek script wordt geladen en de aanroepen
     `.call(this, …)` gebruiken; laadt iemand het ooit als module, dan is `this`
     undefined en gooit ELKE fetch op de site "Illegal invocation". */
  const echteFetch = window.fetch.bind(window);
  window.fetch = function (bron, opties) {
    try {
      const adres = typeof bron === 'string' ? bron : bron && bron.url;
      if (adres && /\/search\/suggest/.test(adres) && !/resources(%5B|\[)type/.test(adres)) {
        const u = new URL(adres, location.origin);
        u.searchParams.set('resources[type]', SOORTEN);
        u.searchParams.set('resources[limit]', PER_SOORT);
        return echteFetch(u.toString(), opties);
      }
    } catch (e) {
      /* stil: liever zoeken zonder pagina's dan niet zoeken */
    }
    return echteFetch(bron, opties);
  };

  /* ── het aantal op de knop ──────────────────────────────────────────────
     HET EERSTE GETAL WAS EEN LEUGEN. Ik las het uit de regel die een schermlezer
     voorleest ("8 search results found for …"). Maar die telt alleen de kaarten
     die `/search/suggest` terugstuurde, en dat zijn er bij ons hoogstens vier per
     soort. Gemeten op 11 aug 2026: "women" beloofde 8, de zoekpagina toonde 113;
     "dress" beloofde 8 tegen 44. Een knop die te weinig belooft is erger dan een
     knop zonder getal.

     Het echte aantal komt nu uit `templates/search.telling.liquid`: een sjabloon
     zonder opmaak dat alleen `search.results_count` teruggeeft. Een paar tekens
     in plaats van de 233 kB die de sectie kost.

     Bij precies één resultaat blijft het bij "View all": een aantal van één is
     ruis, en de knop brengt je naar diezelfde ene kaart. */
  const tellingen = new Map();
  let tellingTimer = null;

  function huidigeTerm() {
    const veld = document.querySelector('.predictive-search-form input[name="q"], #search-modal input[name="q"]');
    return veld ? veld.value.trim() : '';
  }

  function knopSchrijven(term) {
    document.querySelectorAll('.predictive-search-form__footer').forEach((voet) => {
      const knop = voet.querySelector('button, a');
      if (!knop) return;
      if (!knop.dataset.hzoBasis) knop.dataset.hzoBasis = knop.textContent.trim();
      const basis = knop.dataset.hzoBasis || 'View all';

      const aantal = tellingen.get(term);
      const tekst = aantal && aantal > 1 ? `${basis} ${aantal} results` : basis;
      if (knop.textContent.trim() !== tekst) knop.textContent = tekst;
    });
  }

  function tellingOphalen(term) {
    if (!term || tellingen.has(term)) return;
    clearTimeout(tellingTimer);
    /* Wachten tot het typen stilvalt: anders halen we bij "women" vijf keer een
       aantal op voor een woord dat nog niet af is. */
    tellingTimer = setTimeout(() => {
      const u = new URL(location.origin + '/search');
      u.searchParams.set('q', term);
      u.searchParams.set('view', 'telling');
      echteFetch(u.toString())
        .then((r) => (r.ok ? r.text() : null))
        .then((tekst) => {
          if (tekst === null) return;
          const n = parseInt(String(tekst).replace(/[^\d]/g, ''), 10);
          if (!Number.isFinite(n)) return;
          tellingen.set(term, n);
          /* alleen schrijven als de bezoeker nog steeds dít zoekt — anders zet je
             het getal van een woord dat al weg is */
          if (huidigeTerm() === term) knopSchrijven(term);
        })
        .catch(() => {});
    }, 450);
  }

  /* ── niets gevonden ─────────────────────────────────────────────────────
     Horizon zet bij nul resultaten ALLEEN een regel neer die een schermlezer
     voorleest. Wie kijkt ziet een lege witte strook van 32px en verder niets —
     geen boodschap, geen uitweg. Gemeten met "size": het paneel bevatte één
     `.visually-hidden` en anders niets.

     De boodschap zelf halen we uit die regel: dan staat de zoekterm er altijd
     goed in, ook als hij morgen anders geschreven wordt. De uitwegen komen uit
     het echte hoofdmenu.

     WAAROM DIT IN DE VOETTEKST STAAT EN NIET IN HET PANEEL. Bij elke aanslag
     doet Horizon `morph()` op het paneel, dus alles wat we daar neerzetten is
     meteen weg. Erger: onze waarnemer kijkt naar dat paneel, dus erin schrijven
     laat hem zichzelf voeden — precies de lus die eerder in dit project een
     tabblad platlegde. De voettekst is een buurelement en valt buiten beide. */
  function uitwegen() {
    /* DE EERSTE VERSIE KOOS "TOPS" EN PLAKTE ER "151 RESULTS" ACHTER.
       Twee fouten in één regel. Ik pakte élke `/collections/<één-segment>`-link
       op de pagina: `mens-tops` is óók één segment, dus een subitem kwam als
       hoofdafdeling naar voren. En `.menu-list a` bleek ook binnen het
       zoekvenster te matchen, waar de link "Men 151 results" heet.

       Nu per top-niveau menu-item de eerste collectie-link. Men en Women zijn
       uitklappers, dus hun link is geen direct kind van de <li> — vandaar
       `querySelector` binnen het item en niet een `>`-selector. */
    const items = [...document.querySelectorAll('.header-menu .menu-list > overflow-list > li.menu-list__list-item')];
    const gezien = new Set();
    const lijst = [];
    items.forEach((li) => {
      if (lijst.length >= 2) return;
      const a = li.querySelector('a[href^="/collections/"]');
      if (!a) return;
      const href = a.getAttribute('href');
      const tekst = a.textContent.trim().replace(/\s+/g, ' ');
      if (!tekst || gezien.has(href)) return;
      gezien.add(href);
      lijst.push({ tekst, href });
    });
    if (!gezien.has('/collections/all')) lijst.push({ tekst: 'Shop all', href: '/collections/all' });
    return lijst;
  }

  function leegBijwerken(term) {
    const voet = document.querySelector('.predictive-search-form__footer');
    const paneel = document.querySelector('#predictive-search-results, [data-search-results]');
    /* NIET in de voettekst. Die staat `position: absolute` tegen de onderrand
       van het formulier vastgeplakt, dus een blok van 151px groeide omhoog —
       recht achter de zoekbalk, die er sticky overheen ligt. De kop en de
       tekstregel stonden er wél, maar onzichtbaar; alleen de drie knoppen
       staken er onderuit. Gemeten: kop op 76px terwijl het venster op 70 begint
       en de balk tot 121 loopt.

       De ouder van het paneel stroomt gewoon mee en valt nog steeds BUITEN de
       waarnemer (die kijkt naar het paneel zelf, niet naar zijn ouder). */
    const huis = paneel && paneel.parentElement;
    if (!huis) return;

    const heeftKaarten = !!(paneel && paneel.querySelector('.predictive-search-results__card'));
    const leeg = !!term && !heeftKaarten;

    let blok = huis.querySelector(':scope > .hzo-zoek-leeg');
    if (!leeg) {
      if (blok) blok.hidden = true;
      if (voet) voet.querySelectorAll('button, a').forEach((k) => { k.hidden = false; });
      return;
    }

    /* de knop "View all" wijst bij nul resultaten naar een even lege pagina */
    if (voet) voet.querySelectorAll('button, a').forEach((k) => { k.hidden = true; });

    if (!blok) {
      blok = document.createElement('div');
      blok.className = 'hzo-zoek-leeg';
      huis.appendChild(blok);
    }
    blok.hidden = false;

    const melding = paneel.querySelector('.visually-hidden');
    const kop = (melding && melding.textContent.trim()) || `No results found for “${term}”`;
    if (blok.dataset.hzoKop !== kop) {
      blok.dataset.hzoKop = kop;
      blok.textContent = '';

      const h = document.createElement('p');
      h.className = 'hzo-zoek-leeg__kop';
      h.textContent = kop;
      blok.appendChild(h);

      const p = document.createElement('p');
      p.className = 'hzo-zoek-leeg__tekst';
      p.textContent = 'Check the spelling, or start here:';
      blok.appendChild(p);

      const rij = document.createElement('div');
      rij.className = 'hzo-zoek-leeg__rij';
      uitwegen().forEach((u) => {
        const a = document.createElement('a');
        a.className = 'hzo-zoek-leeg__knop';
        a.href = u.href;
        a.textContent = u.tekst;
        rij.appendChild(a);
      });
      blok.appendChild(rij);
    }
  }

  function knopBijwerken() {
    const term = huidigeTerm();
    knopSchrijven(term);
    tellingOphalen(term);
    leegBijwerken(term);
  }

  /* HET AANTAL LIEP EEN AANSLAG ACHTER. Ik werkte de knop bij op vaste tijden
     (350 en 900 ms), maar het antwoord komt er pas na ruim twee seconden. Je zag
     dan het getal van je vórige zoekterm — erger dan geen getal.

     Dus kijken wanneer het paneel écht verandert. Een MutationObserver mag hier
     wél: wij schrijven in de VOETTEKST, en die staat buiten het paneel dat we
     bekijken. De waarnemer kan zichzelf dus niet voeden — dat is precies de val
     die eerder in dit project een tabblad platlegde.

     WAAROM EEN WeakSet EN GEEN data-ATTRIBUUT. Hier stond `dataset.hzoBekeken`.
     Vervangt Horizon het paneel in plaats van het bij te werken, dan reist dat
     attribuut mee naar het nieuwe element — maar de waarnemer niet. De vlag zegt
     dan "wordt bekeken" terwijl er niemand meer kijkt, en het aantal op de knop
     bevriest voor de rest van het bezoek. Een WeakSet verwijst naar het element
     zelf en kan dus niet meegekopieerd worden. */
  const bekekenPanelen = new WeakSet();

  function kijken() {
    const paneel = document.querySelector('#predictive-search-results, [data-search-results]');
    if (!paneel || bekekenPanelen.has(paneel)) return;
    bekekenPanelen.add(paneel);
    new MutationObserver(() => knopBijwerken()).observe(paneel, { childList: true, subtree: true });
    knopBijwerken();
  }

  /* DEZE TWEE VUURDEN NOOIT. Ik mikte op `.predictive-search`, maar die klasse
     bestaat in Horizon niet: het custom element heet `predictive-search-component`
     en de klassen zijn `predictive-search-form...`. Het aantal klopte alleen
     omdat de waarnemer hieronder al bij het laden van de pagina wordt gehangen —
     zodra Horizon het paneel later zou aanmaken, was er niets meer dat opnieuw
     probeerde. Nu op wat er echt staat. */
  const ZOEKER = 'predictive-search-component, .predictive-search-form, search-button, #search-modal';
  document.addEventListener('click', (e) => {
    if (e.target.closest?.(ZOEKER)) setTimeout(kijken, 400);
  });
  document.addEventListener('input', (e) => {
    if (e.target.closest?.(ZOEKER)) kijken();
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', kijken, { once: true });
  } else {
    kijken();
  }
})();
